import jwtDecode from 'jwt-decode'
import { getPersisted, setPersisted } from './helpers'

// BE rotuje REFRESH_TOKEN při každém refreshi a nezvládá souběh: dva paralelní refreshe se stejnou
// hodnotou dostanou každý JINOU novou hodnotu, ale platná zůstane jen jedna. Prohlížeč si uloží tu
// z odpovědi, která dorazí poslední, takže může skončit s neplatnou → další refresh selže na
// E_SECURITY_EXCEPTION → session (a s ní košík) je nevratně ztracená.
// Proto musí být v jednu chvíli v letu maximálně jeden refresh.

const LOCK_NAME = 'cs-token-refresh'

// O kolik dřív před expirací se token obnovuje proaktivně. JWT z websale api_key žije 60 s.
export const REFRESH_MARGIN_MS = 10_000

// Strop proti refresh stormu, kdyby expiraci nešlo spolehlivě určit (např. posunuté hodiny klienta).
export const MIN_PROACTIVE_INTERVAL_MS = 20_000

let inFlight = null
let lastProactiveAt = 0
let proactiveDisabled = false

// Určeno pro testy – modulový stav mezi nimi neteče.
export function resetRefreshState() {
    inFlight = null
    lastProactiveAt = 0
    proactiveDisabled = false
}

const expiresAtFromToken = (token) => {
    try {
        const { exp } = jwtDecode(token)

        return typeof exp === 'number' ? exp * 1000 : null
    } catch {
        return null
    }
}

// Expiraci čteme z `exp` v JWT, ne z cookie token_validity. Hlavní web zapisuje sdílenou cookie
// `token`, ale `token_validity` vůbec nezná – ta by tedy po průchodu portálem popisovala starší
// token a hlásila expiraci u čerstvého. `exp` vždycky popisuje token, který skutečně držíme.
export function tokenExpiresSoon() {
    const token = getPersisted('token')
    if (!token) {
        return false
    }

    const expiresAt = expiresAtFromToken(token)
    if (expiresAt === null) {
        return false
    }

    return expiresAt - Date.now() < REFRESH_MARGIN_MS
}

async function performAndStore(performRefresh) {
    // Výjimky (síťová chyba, timeout) záměrně propouštíme dál – stejně jako před touto změnou.
    // Kdybychom je spolkli, propadlo by to na createToken a zabilo session, která je v pořádku.
    const resp = await performRefresh()

    const token = resp?.data?.token
    if (!token) {
        return ''
    }

    setPersisted('token', token)
    if (resp.data.validity) {
        setPersisted('token_validity', resp.data.validity)
    }

    // Čerstvě vydaný token nesmí hlásit blížící se expiraci. Když ano, hodiny klienta jsou pro
    // tohle rozhodování nepoužitelné – vypnout proaktivní režim, ať z toho není refresh storm.
    if (tokenExpiresSoon()) {
        proactiveDisabled = true
    }

    return token
}

async function runExclusively(knownToken, performRefresh) {
    // Web Locks jsou per-origin, takže sladí všechny taby websale. Chybí ve starých prohlížečích
    // a mimo secure context – tam zbyde single-flight v rámci tabu.
    if (!navigator.locks?.request) {
        return performAndStore(performRefresh)
    }

    return navigator.locks.request(LOCK_NAME, async () => {
        // Než jsme dostali zámek, mohl token vyměnit jiný tab. Porovnáváme konkrétní hodnotu, ne
        // expiraci – jinak by se dal zkratovat i refresh, který token odmítl z jiného důvodu než
        // expirace, a retry by se zacyklil na tom samém tokenu.
        const current = getPersisted('token')
        if (current && current !== knownToken) {
            return current
        }

        return performAndStore(performRefresh)
    })
}

/**
 * Obnoví token nejvýš jednou napříč souběžnými volajícími i napříč taby.
 * Vrací nový (nebo mezitím cizím tabem obnovený) token, prázdný string když BE token nevydal.
 * Síťové chyby propouští dál.
 */
export function refreshToken(performRefresh) {
    if (inFlight) {
        return inFlight
    }

    const knownToken = getPersisted('token')

    inFlight = runExclusively(knownToken, performRefresh).finally(() => {
        inFlight = null
    })

    return inFlight
}

/**
 * Obnoví token ještě před odesláním requestu, pokud se blíží expirace.
 * Best-effort: nikdy nevyhodí výjimku, aby selhání neblokovalo odeslání requestu.
 */
export async function maybeRefreshProactively(performRefresh) {
    if (proactiveDisabled || process.env.REACT_APP_DISABLE_PROACTIVE_REFRESH === 'true') {
        return
    }

    if (!tokenExpiresSoon()) {
        return
    }

    if (Date.now() - lastProactiveAt < MIN_PROACTIVE_INTERVAL_MS) {
        return
    }

    lastProactiveAt = Date.now()

    try {
        await refreshToken(performRefresh)
    } catch {
        // Reaktivní cesta v response interceptoru to vyřeší jako dosud.
    }
}
